import { useDispatch } from 'react-redux';

import { DeleteItemsModal } from 'ui/indexPages/modals/DeleteItemsModal';
import { Archive } from 'ui/icons';

import { archiveCampaignsRequest } from '../actions';
import { Campaign } from '../domainTypes';

/** Configures the `<DeleteItemsModal />` to show campaign-specific text */
const displayTextForArchivingCampaigns = {
  actionName: {
    verb: 'archive',
    adjective: 'archived',
  },
  itemTypeName: {
    singular: 'campaign',
    plural: 'campaigns',
  },
  actionIcon: Archive,
};

/**
 * Props to be passed to the `<ArchiveCampaignModal />` component
 */
type ArchiveCampaignProps = {
  /** Campaign object(s) that are being archived. */
  campaigns: Campaign[];

  /** Callback function that is invoked when the user confirms or cancels the archive modal. */
  onClose: () => void;

  /** Callback function that is invoked after the user confirms the archive modal. Used for refetching GraphQL resutls after archiving. */
  refetch: () => void;

  /** Callback function that is invoked after the user confirms the archive modal. Should deselect all campaigns. */
  deselectAll: () => void;
};

/**
 * A modal to be shown when archiving campaign(s) on the campaign index page. Contains logic
 * to delete the folder when the user hits the "confirm" button.
 */
export const ArchiveCampaignModal = ({
  campaigns = [],
  onClose = () => null,
  refetch = () => null,
  deselectAll = () => null,
}: ArchiveCampaignProps) => {
  const dispatch = useDispatch();

  const handleConfirmArchive = () => {
    onClose();
    deselectAll();
    dispatch(
      archiveCampaignsRequest(
        campaigns.map((campaign) => campaign.id),
        refetch,
      ),
    );
  };

  return (
    <DeleteItemsModal
      onClose={onClose}
      onConfirm={handleConfirmArchive}
      displayTextForContentItems={displayTextForArchivingCampaigns}
      content={campaigns}
      data-test="archive-campaign-modal"
    />
  );
};
